import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import axios from "axios";
import { Api } from "../../../services/api";
import './EmailDialog.css'

const EmailDialog = ({
  visible,
  onHide,
  showName,
  showId,
  allSupervisors,
  toast,
}) => {
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [selectedCCList, setSelectedCCList] = useState([]);
  const [emailThreads, setEmailThreads] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null); // stores thread/message being replied to
  const [expandedMessages, setExpandedMessages] = useState({});

  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (visible && showId) {
      fetchEmailThreads(showId);
      setReplyingTo(null); // reset reply when dialog opens
    }
  }, [visible, showId]);

  const fetchEmailThreads = async (leadId) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      };
      const res = await axios.get(
        `${Api.API_BASE_URL}crm/get-lead-mail/${leadId}`,
        { headers }
      );
      setEmailThreads(res.data.result || []);
    } catch (error) {
      console.error("Error fetching email history:", error);
      setEmailThreads([]);
    }
  };

  const toggleExpand = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!customSubject || !customMessage) return;

    const emailData = {
      id: showId,
      to: showName,
      subject: customSubject,
      message: customMessage,
      cc: selectedCCList.map((user) => user.user_id),
      in_reply_to: replyingTo?.messages?.slice(-1)[0]?.message_id || null,
      thread_number: replyingTo?.thread_number || null,
    };

    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      };
      const response = await axios.post(
        Api.API_BASE_URL + "crm/send-lead-mail",
        emailData,
        { headers }
      );

      toast.current.show({
        severity: "success",
        detail: response.data.message || "Email sent successfully",
        life: 3000,
      });

      // Append new message to the thread or create new thread
      const newMessage = {
        _id: Date.now(),
        lead_id: showId,
        cc_list: selectedCCList.map((u) => u.user_id),
        message: customMessage,
        subject: customSubject,
        direction: "outgoing",
        from_email: "You",
        to_email: [showName],
        message_id: response.data.message_id || null,
        send_at: new Date().toISOString(),
      };

      if (replyingTo) {
        // add to existing thread
        const updatedThreads = emailThreads.map((thread) =>
          thread.thread_number === replyingTo.thread_number
            ? { ...thread, messages: [...thread.messages, newMessage] }
            : thread
        );
        setEmailThreads(updatedThreads);
      } else {
        // new thread
        const newThread = {
          thread_number:
            emailThreads.length > 0
              ? Math.max(...emailThreads.map((t) => t.thread_number)) + 1
              : 1,
          subject: customSubject,
          messages: [newMessage],
        };
        setEmailThreads([newThread, ...emailThreads]);
      }

      // Reset draft box
      setCustomSubject("");
      setCustomMessage("");
      setSelectedCCList([]);
      setReplyingTo(null);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.current.show({
        severity: "error",
        detail: "An error occurred while sending the email.",
        life: 3000,
      });
    }
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-envelope text-yellow-700 text-lg"></i>
      <span className="text-lg font-semibold text-yellow-700">
        Email Correspondence with {showName}
      </span>
    </div>
  );

  const handleGenerateDraft = async () => {
    if (!aiPrompt) return;
    setIsGenerating(true);

    try {
      const response = await fetch("/api/openai-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();
      if (data?.draft) {
        setCustomMessage(data.draft); // populate the Editor with AI draft
      }
    } catch (error) {
      console.error("Error generating AI draft:", error);
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ width: "50vw", maxWidth: "800px" }}
      dismissableMask
      onHide={onHide}
      className="email-dialog"
    >
      <div className="p-3 email-dialog-container">
        {/* Show draft box if no previous emails or replying */}
        {(!emailThreads.length || replyingTo) && (
          <form
            onSubmit={handleSendEmail}
            className="flex flex-column gap-3 mb-4"
          >
            {/* To Field */}
            <div className="flex flex-column">
              <label className="font-medium text-gray-700">
                To: <strong>{showName}</strong>
              </label>
            </div>

            {/* CC Field */}
            <div className="flex flex-column">
              <label className="font-medium text-gray-700 mb-2">CC:</label>
              {/* <MultiSelect
                value={selectedCCList}
                onChange={(e) => setSelectedCCList(e.value)}
                options={allSupervisors}
                optionLabel={(e) =>
                  `${e.first_name} ${e.last_name} - ${e.role_name}`
                }
                display="chip"
                filter
                placeholder="Select CC recipients"
                className="p-inputtext-sm border-round-md"
              /> */}

              <MultiSelect
                value={selectedCCList}
                onChange={(e) => setSelectedCCList(e.value)}
                options={allSupervisors}
                optionLabel={(e) =>
                  `${e.first_name} ${e.last_name} - ${e.role_name}`
                }
                display="chip"
                filter
                placeholder="Select CC recipients"
                className="w-full border-round-md custom-input-height"
                style={{ minHeight: "38px" }}
              />
            </div>

            {/* Subject */}
            <div className="flex flex-column">
              <label
                htmlFor="customSubject"
                className="font-medium text-gray-700 mb-2"
              >
                Subject
              </label>

              <InputText
                id="customSubject"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Enter email subject"
                className="w-full border-round-md custom-input-height"
              />
            </div>

            {/* AI Prompt Input */}
            <div className="flex flex-column mb-3">
              <label
                htmlFor="aiPrompt"
                className="font-medium text-gray-700 mb-2"
              >
                AI Draft Prompt
              </label>
              <InputText
                id="aiPrompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Enter prompt for AI to draft your email"
                className="w-full border-round-md custom-input-height"
              />
              <div className="flex justify-content-end">
                {" "}
                {/* 👈 ADDED WRAPPER */}
                <Button
                  label={isGenerating ? "Generating..." : "Generate Draft"}
                  icon="pi pi-robot"
                  disabled={!aiPrompt || isGenerating}
                  className="mt-2 customSave-button px-4 py-2 border-round-md"
                  style={{ width: "auto" }}
                  onClick={handleGenerateDraft}
                />
              </div>{" "}
            </div>

            {/* Message Editor */}
            <div className="flex flex-column">
              <label
                htmlFor="customMessage"
                className="font-medium text-gray-700 mb-2"
              >
                Message
              </label>
              <Editor
                id="customMessage"
                value={customMessage}
                onTextChange={(e) => setCustomMessage(e.htmlValue)}
                style={{
                  height: "200px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                }}
                className="p-inputtext-sm"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Cancel"
                type="button"
                outlined
                className="px-4 py-2 border-round-md"
                severity="secondary"
                onClick={() => setReplyingTo(null)}
              />
              <Button
                label="Send"
                type="submit"
                className="customSave-button px-4 py-2 border-round-md"
                disabled={!customSubject || !customMessage}
              />
            </div>
          </form>
        )}

        {/* Divider */}
        {emailThreads.length > 0 && <hr className="my-4 border-gray-300" />}

        {/* Email Threads */}
        <h5 className="Leads-heading mb-3">Email History</h5>
        {emailThreads.length === 0 ? (
          <p className="text-gray-500 text-sm">No previous emails found.</p>
        ) : (
          <div
            className="max-h-60vh overflow-auto p-2 border-1 border-round surface-border"
            style={{ background: "#f9f9f9" }}
          >
            {/* {emailThreads.map((thread) => ( */}
            {[...emailThreads].reverse().map((thread) => (
              <div key={thread.thread_number} className="mb-5">
                <h6 className="font-semibold mb-2">
                  Thread #{thread.thread_number}
                </h6>
                <p className="mb-3">
                  <strong>Subject:</strong> {thread.subject}
                </p>

                {[...thread.messages].reverse().map((msg, i) => (
                  <div
                    key={i}
                    className={`p-3 mb-2 border-round-md border-1 surface-border shadow-sm ${
                      msg.direction === "incoming" ? "bg-blue-50" : "bg-white"
                    }`}
                    style={{
                      borderLeft:
                        msg.direction === "incoming"
                          ? "4px solid #3b82f6"
                          : "4px solid #10b981",
                    }}
                  >
                    <div className="flex justify-content-between text-xs text-gray-500 mb-2">
                      <span>
                        <strong>
                          {msg.direction === "outgoing"
                            ? "Sent to"
                            : "Received from"}
                          :
                        </strong>{" "}
                        {msg.direction === "outgoing"
                          ? msg.to_email.join(", ")
                          : msg.from_email}
                      </span>
                      <span>{new Date(msg.send_at).toLocaleString()}</span>
                    </div>

                    <div className="text-sm text-gray-800">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: expandedMessages[msg._id]
                            ? msg.message
                            : msg.message.split(" ").slice(0, 25).join(" ") +
                              (msg.message.split(" ").length > 25 ? "..." : ""),
                        }}
                        style={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: expandedMessages[msg._id]
                            ? "none"
                            : 3,
                        }}
                      />

                      {msg.message.split(" ").length > 25 && (
                        <Button
                          label={
                            expandedMessages[msg._id]
                              ? "Show less"
                              : "Show more"
                          }
                          text
                          className="p-0 text-sm text-blue-500 hover:underline mt-1"
                          onClick={() => toggleExpand(msg._id)}
                        />
                      )}
                    </div>

                    {/* ✅ Reply button next to latest message only */}
                    {!replyingTo && i === 0 && (
                      <div className="mt-2 text-right">
                        <Button
                          label="Reply"
                          icon="pi pi-reply"
                          className="p-button-sm"
                          onClick={() => {
                            setReplyingTo(thread);
                            setCustomSubject(`Re: ${thread.subject}`);
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default EmailDialog;
